import { z } from 'zod';

export const roleSchema = z.enum(['USER', 'ADMIN']);
export type Role = z.infer<typeof roleSchema>;

/** Interface languages; German is the reference, the others must cover every text. */
export const LOCALES = ['de', 'en', 'pl'] as const;
export const localeSchema = z.enum(LOCALES);
export type Locale = z.infer<typeof localeSchema>;

const email = z.string().trim().toLowerCase().pipe(z.email().max(254));

export const PASSWORD_MIN_LENGTH = 10;

export const loginRequestSchema = z.object({
  email,
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const registerRequestSchema = z.object({
  email,
  password: z.string().min(PASSWORD_MIN_LENGTH).max(256),
  displayName: z.string().trim().min(1).max(100),
  /** Language chosen on the sign-in page; stored with the new account. */
  locale: localeSchema.optional(),
  /** Shared code handed out to invited people; required only when the server sets one. */
  accessCode: z.string().max(100).optional(),
  /** Bot trap: hidden from people, so any content marks the request as automated. */
  website: z.string().max(500).optional(),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const accessCodeRequestSchema = z.object({ code: z.string().max(100) });
export type AccessCodeRequest = z.infer<typeof accessCodeRequestSchema>;

export const userDtoSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: roleSchema,
  /** Null until the user picks a language; the browser language applies meanwhile. */
  locale: localeSchema.nullable(),
});
export type UserDto = z.infer<typeof userDtoSchema>;

export const updateMeRequestSchema = z.object({ locale: localeSchema });
export type UpdateMeRequest = z.infer<typeof updateMeRequestSchema>;

export const meResponseSchema = z.object({ user: userDtoSchema });
export type MeResponse = z.infer<typeof meResponseSchema>;

/** Public sign-in options: self-registration, and whether new accounts need an admin's approval. */
export const authOptionsSchema = z.object({
  allowRegistration: z.boolean(),
  requiresApproval: z.boolean(),
  /** The sign-in page asks for the shared access code before showing its forms. */
  accessCodeRequired: z.boolean(),
});
export type AuthOptions = z.infer<typeof authOptionsSchema>;

/** Either signed in right away or waiting for approval (no session then). */
export const registerResponseSchema = z.union([
  meResponseSchema,
  z.object({ pending: z.literal(true) }),
]);
export type RegisterResponse = z.infer<typeof registerResponseSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: z.string().min(PASSWORD_MIN_LENGTH).max(256),
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;
